/**
 * useScrollEdgeState — shared scroll-edge affordance primitive.
 *
 * Measures the real scrollport (`ref`) and writes `data-scroll-up` /
 * `data-scroll-down` so CSS renders a top/bottom fade ONLY when the matching
 * edge truly overflows.
 *
 * `wrapperRef` mirrors those attributes onto a NON-SCROLLING wrapper, and that
 * wrapper is where `.scroll-edge-affordance` belongs. Reason: an absolutely
 * positioned pseudo-element whose containing block is a scrollport scrolls WITH
 * the content, so `top: 0` / `bottom: 0` anchor to the scrolled content box
 * instead of the visible viewport. Measured on the 390x844 rail: a probe pinned
 * to `top: 0` inside the scrollport drifted -91px at scrollTop 91 and -182px at
 * scrollTop 182, i.e. the cue detached from the edge it was meant to mark. A
 * `relative overflow-hidden` wrapper has no scroll offset, so the same
 * pseudo-elements stay pinned to the visible edge and are clipped to the
 * viewport.
 *
 * Read-only measurement; scroll events are coalesced through rAF. Both element
 * resizes and content mutations re-measure, so a static menu can never keep a
 * stale initial reading.
 *
 * [t-76e90519] Carried verbatim from the shared dirty checkout's untracked
 * `useScrollEdgeState.ts` (see `git status` on `/home/desendo/.../fintrack`)
 * so the approved TransactionDateRangeControl.tsx edit can compile inside
 * this isolated worktree. The hook is also referenced by `AppLayout.tsx`
 * which sits on the dirty sidebar saga and is OUTSIDE this task's approved
 * modify list — touching AppLayout is NOT required for this task and is
 * explicitly NOT done here.
 */

import { useCallback, useEffect, useRef } from 'react';

const EDGE_ATTRIBUTES = ['data-scroll-up', 'data-scroll-down'] as const;
// Hidden-pixel counts, published so CSS can cap each cue at the amount of content
// it actually hides. A flat 24px band over 6px of hidden rail reads as clipping.
const EDGE_PROPERTIES = ['--scroll-up-px', '--scroll-down-px'] as const;

function clearEdgeAttributes(node: HTMLElement | null): void {
  if (!node) return;
  for (const attribute of EDGE_ATTRIBUTES) node.removeAttribute(attribute);
  for (const property of EDGE_PROPERTIES) node.style.removeProperty(property);
}

export interface ScrollEdgeState<T extends HTMLElement, W extends HTMLElement> {
  /** Attach to the element that owns `overflow-y-auto`. */
  ref: (node: T | null) => void;
  /** Attach to the non-scrolling `relative overflow-hidden` fade wrapper. */
  wrapperRef: (node: W | null) => void;
}

export function useScrollEdgeState<
  T extends HTMLElement = HTMLElement,
  W extends HTMLElement = HTMLElement,
>(): ScrollEdgeState<T, W> {
  const scrollportRef = useRef<T | null>(null);
  const wrapperRefValue = useRef<W | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const rafRef = useRef(0);

  const compute = useCallback(() => {
    const node = scrollportRef.current;
    if (!node) return;
    const upPx = Math.max(0, Math.round(node.scrollTop));
    const downPx = Math.max(0, Math.round(node.scrollHeight - node.clientHeight - node.scrollTop));
    const up = upPx > 0 ? 'true' : 'false';
    const down = downPx > 1 ? 'true' : 'false';
    for (const target of [node as HTMLElement, wrapperRefValue.current]) {
      if (!target) continue;
      target.setAttribute('data-scroll-up', up);
      target.setAttribute('data-scroll-down', down);
      target.style.setProperty('--scroll-up-px', `${upPx}px`);
      target.style.setProperty('--scroll-down-px', `${downPx}px`);
    }
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current !== 0) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      compute();
    });
  }, [compute]);

  const ref = useCallback(
    (node: T | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      scrollportRef.current = node;
      if (!node) {
        clearEdgeAttributes(wrapperRefValue.current);
        return;
      }
      // A scrollport's own box does not resize when its content grows, so
      // `scrollHeight` changes are only observable through its children and
      // through DOM mutations (a nav badge mounting, a preset group swapping).
      const resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(node);
      const observeChildren = () => {
        for (const child of Array.from(node.children)) resizeObserver.observe(child);
      };
      observeChildren();
      const mutationObserver = new MutationObserver(() => {
        observeChildren();
        schedule();
      });
      mutationObserver.observe(node, { childList: true, subtree: true });
      node.addEventListener('scroll', schedule, { passive: true });
      compute();
      cleanupRef.current = () => {
        node.removeEventListener('scroll', schedule);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        if (rafRef.current !== 0) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
        }
        clearEdgeAttributes(node);
        clearEdgeAttributes(wrapperRefValue.current);
      };
    },
    [compute, schedule],
  );

  const wrapperRef = useCallback(
    (node: W | null) => {
      const previous = wrapperRefValue.current;
      if (previous && previous !== node) clearEdgeAttributes(previous);
      wrapperRefValue.current = node;
      // Sync immediately when the wrapper mounts after the scrollport; when it
      // mounts first this is a no-op and the scrollport's own compute covers it.
      if (node) compute();
    },
    [compute],
  );

  useEffect(() => () => cleanupRef.current?.(), []);

  return { ref, wrapperRef };
}
